import { useEffect, useState } from "react";
import axios from "axios";
import { LogOut, Trash2, Pencil, Check, X, Lock, ImagePlus, Plus } from "lucide-react";
import LogoMark from "@/components/LogoMark";
import { productImage } from "@/components/ProductGrid";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "apsara_admin_token";

function client(token) {
  return axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

function formatErr(e) {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").join(" ");
  return "Etwas ist schiefgelaufen — bitte erneut versuchen.";
}

const inputCls =
  "w-full rounded-lg border border-gold/30 bg-wine2/60 px-4 py-2.5 text-sm text-cream placeholder:text-cream/30 outline-none transition-colors duration-300 focus:border-gold";

function LoginView({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      onLogin(data.token);
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-wine px-5">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-2xl border border-gold/20 bg-wine2/40 p-8 backdrop-blur-md"
        data-testid="admin-login-form"
      >
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <LogoMark className="h-10 w-10" />
          <h1 className="font-serif text-3xl text-cream">
            Admin<span className="text-gold">.</span>
          </h1>
          <p className="text-xs font-light uppercase tracking-[0.3em] text-cream/50">
            APSARA Kollektion
          </p>
        </div>
        <label className="mb-2 block text-xs font-light uppercase tracking-[0.25em] text-gold">
          Passwort
        </label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          className={inputCls}
          data-testid="admin-password-input"
        />
        {error && (
          <p className="mt-3 text-sm text-red-400" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-medium uppercase tracking-widest text-wine transition-colors duration-300 hover:bg-goldlt disabled:opacity-50"
          data-testid="admin-login-submit"
        >
          <Lock className="h-4 w-4" />
          {busy ? "Prüfe…" : "Einloggen"}
        </button>
      </form>
    </div>
  );
}

function Row({ p, token, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(p.name);
  const [price, setPrice] = useState(p.price);
  const [desc, setDesc] = useState(p.description || "");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("type", p.type);
      fd.append("name", name);
      fd.append("price", price);
      fd.append("description", desc);
      if (file) fd.append("file", file);
      await client(token).put(`${API}/admin/products/${p.id}`, fd);
      setEditing(false);
      setFile(null);
      onChanged();
    } catch (e) {
      alert(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`„${p.name}" wirklich löschen?`)) return;
    try {
      await client(token).delete(`${API}/admin/products/${p.id}`);
      onChanged();
    } catch (e) {
      alert(formatErr(e));
    }
  };

  const src = productImage(p.image);

  return (
    <div
      className="flex flex-col gap-4 rounded-xl border border-gold/15 bg-wine2/40 p-4 sm:flex-row sm:items-center"
      data-testid={`admin-row-${p.id}`}
    >
      <div className="h-24 w-20 shrink-0 overflow-hidden rounded-lg border border-white/10 bg-wine">
        {src ? (
          <img src={src} alt={p.name} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center text-cream/30">
            <ImagePlus className="h-5 w-5" />
          </div>
        )}
      </div>

      {editing ? (
        <div className="flex-1 space-y-3">
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid={`admin-name-input-${p.id}`} />
          <input value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Preis" className={inputCls} data-testid={`admin-price-input-${p.id}`} />
          <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} className="block w-full text-xs text-cream/50 file:mr-3 file:rounded-full file:border-0 file:bg-gold/20 file:px-3 file:py-1.5 file:text-xs file:text-gold" data-testid={`admin-image-input-${p.id}`} />
          <textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Beschreibung (optional)" rows={2} className={inputCls} data-testid={`admin-description-input-${p.id}`} />
          <div className="flex gap-2">
            <button onClick={save} disabled={busy} className="flex items-center gap-1.5 rounded-full bg-gold px-4 py-1.5 text-xs font-medium uppercase tracking-widest text-wine transition-colors duration-300 hover:bg-goldlt disabled:opacity-50" data-testid={`admin-save-${p.id}`}>
              <Check className="h-3.5 w-3.5" /> Speichern
            </button>
            <button onClick={() => { setEditing(false); setName(p.name); setPrice(p.price); setDesc(p.description || ""); setFile(null); }} className="flex items-center gap-1.5 rounded-full border border-gold/40 px-4 py-1.5 text-xs font-medium uppercase tracking-widest text-gold transition-colors duration-300 hover:bg-gold hover:text-wine" data-testid={`admin-cancel-${p.id}`}>
              <X className="h-3.5 w-3.5" /> Abbrechen
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-1 flex-col gap-1">
          <p className="font-serif text-lg text-cream" data-testid={`admin-row-name-${p.id}`}>{p.name}</p>
          <p className="text-sm font-light text-gold">{p.price || "Kein Preis"}</p>
          <p className="text-[0.65rem] font-light uppercase tracking-[0.25em] text-cream/40">
            {p.type === "suit" ? "Suit" : p.type === "dupatta" ? "Dupatta" : "Schmuck"}
          </p>
        </div>
      )}

      {!editing && (
        <div className="flex shrink-0 gap-2">
          <button onClick={() => setEditing(true)} className="flex items-center gap-1.5 rounded-full border border-gold/40 px-4 py-1.5 text-xs font-medium uppercase tracking-widest text-gold transition-colors duration-300 hover:bg-gold hover:text-wine" data-testid={`admin-edit-${p.id}`}>
            <Pencil className="h-3.5 w-3.5" /> Bearbeiten
          </button>
          <button onClick={del} className="flex items-center gap-1.5 rounded-full border border-red-400/40 px-4 py-1.5 text-xs font-medium uppercase tracking-widest text-red-400 transition-colors duration-300 hover:bg-red-400 hover:text-wine" data-testid={`admin-delete-${p.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

function AddForm({ token, onChanged }) {
  const [type, setType] = useState("suit");
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [desc, setDesc] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("type", type);
      fd.append("name", name);
      fd.append("price", price);
      fd.append("description", desc);
      if (file) fd.append("file", file);
      await client(token).post(`${API}/admin/products`, fd);
      setName("");
      setPrice("");
      setDesc("");
      setFile(null);
      onChanged();
    } catch (err) {
      alert(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="rounded-xl border border-gold/20 bg-wine2/40 p-5"
      data-testid="admin-add-form"
    >
      <p className="mb-4 flex items-center gap-2 text-xs font-light uppercase tracking-[0.3em] text-gold">
        <Plus className="h-4 w-4" /> Neue Box anlegen
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        <select value={type} onChange={(e) => setType(e.target.value)} className={inputCls} data-testid="admin-add-type">
          <option value="suit">Suit</option>
          <option value="jewelry">Schmuck</option>
          <option value="dupatta">Dupatta</option>
        </select>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (z. B. Phulkari-Suit Grün)" className={inputCls} data-testid="admin-add-name" />
        <input value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Preis (z. B. 75 €)" className={inputCls} data-testid="admin-add-price" />
      </div>
      <textarea
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        placeholder="Beschreibung (optional — erscheint auf der Anzeige-Seite)"
        rows={2}
        className={`${inputCls} mt-3`}
        data-testid="admin-add-description"
      />
      <div className="mt-3 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} className="block text-xs text-cream/50 file:mr-3 file:cursor-pointer file:rounded-full file:border-0 file:bg-gold/20 file:px-3 file:py-1.5 file:text-xs file:text-gold" data-testid="admin-add-image" />
        <button type="submit" disabled={busy} className="flex items-center gap-2 rounded-full bg-gold px-6 py-2.5 text-xs font-medium uppercase tracking-widest text-wine transition-colors duration-300 hover:bg-goldlt disabled:opacity-50" data-testid="admin-add-submit">
          <Plus className="h-4 w-4" /> {busy ? "Lade hoch…" : "Box anlegen"}
        </button>
      </div>
    </form>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [checking, setChecking] = useState(() => !!localStorage.getItem(TOKEN_KEY));
  const [products, setProducts] = useState([]);

  const load = () => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data || [])).catch(() => {});
  };

  useEffect(() => {
    if (!token) return;
    client(token)
      .get(`${API}/admin/me`)
      .then(() => {
        setChecking(false);
        load();
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setChecking(false);
      });
  }, [token]);

  if (!token) {
    return (
      <LoginView
        onLogin={(t) => {
          localStorage.setItem(TOKEN_KEY, t);
          setToken(t);
        }}
      />
    );
  }

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center bg-wine text-cream/50">Lade…</div>;
  }

  const suits = products.filter((p) => p.type === "suit");
  const jewelry = products.filter((p) => p.type === "jewelry");

  return (
    <div className="min-h-screen bg-wine px-5 py-10 text-cream md:px-10" data-testid="admin-panel">
      <div className="mx-auto max-w-4xl">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark className="h-8 w-8" />
            <div>
              <p className="font-serif text-2xl leading-none">
                Admin<span className="text-gold">.</span>
              </p>
              <p className="mt-1 text-[0.6rem] font-light uppercase tracking-[0.35em] text-gold/90">
                APSARA Kollektion
              </p>
            </div>
          </div>
          <a href="/" className="text-xs font-light uppercase tracking-[0.25em] text-cream/50 transition-colors duration-300 hover:text-gold" data-testid="admin-home-link">
            Zur Seite
          </a>
          <button
            onClick={() => {
              localStorage.removeItem(TOKEN_KEY);
              setToken("");
            }}
            className="flex items-center gap-2 rounded-full border border-gold/40 px-5 py-2 text-xs font-medium uppercase tracking-widest text-gold transition-colors duration-300 hover:bg-gold hover:text-wine"
            data-testid="admin-logout"
          >
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>

        <AddForm token={token} onChanged={load} />

        {[
          { title: "Suits", items: suits, testid: "admin-list-suits" },
          { title: "Schmuck", items: jewelry, testid: "admin-list-jewelry" },
        ].map((group) => (
          <div key={group.title} className="mt-10">
            <h2 className="mb-4 font-serif text-3xl font-light text-cream">
              {group.title}{" "}
              <span className="text-base text-cream/40">({group.items.length})</span>
            </h2>
            <div className="space-y-3" data-testid={group.testid}>
              {group.items.map((p) => (
                <Row key={p.id} p={p} token={token} onChanged={load} />
              ))}
              {!group.items.length && (
                <p className="text-sm font-light text-cream/40">Noch keine Boxen hier.</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
